import { type RefObject, useEffect, useRef } from 'react';

/** How long we keep following the last card's growth before leaving the scroll alone. */
const FOLLOW_GROWTH_MS = 2000;

type ScrollToLastMessageOptions = {
  /** The last rendered chat card. */
  ref: RefObject<HTMLDivElement | null>;
  /** Identity of the last visible message; a new value means a new message landed. */
  messageKey: string | null;
  /** Switching conversation scrolls on its own, so we must not fight it. */
  conversationId: string;
};

/**
 * Brings the newest chat card to the top of the reading area, so a reply is read from its
 * first line.
 *
 * This cannot be done from the mutation callbacks: `scrollIntoView` resolves its target
 * once, and an assistant card keeps growing after it mounts — markdown, embedded UI
 * components, charts that size themselves from their container. The scroll would land
 * short. A user card, whose height is final on the first paint, never showed the problem.
 *
 * So: scroll after the commit that rendered the card, then keep re-aligning while its
 * height is still settling — briefly, and never against a user who is scrolling.
 */
export const useScrollToLastMessage = ({ ref, messageKey, conversationId }: ScrollToLastMessageOptions): void => {
  // Seeded with the current values so mounting an existing conversation doesn't animate.
  const previousRef = useRef({ conversationId, messageKey });

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { conversationId, messageKey };

    if (previous.conversationId !== conversationId) return;
    if (!messageKey || messageKey === previous.messageKey) return;

    const node = ref.current;
    if (!node) return;

    const align = () => node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    align();

    const observer = new ResizeObserver(align);
    observer.observe(node);

    const stop = () => {
      observer.disconnect();
      clearTimeout(timer);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchmove', stop);
    };
    const timer = setTimeout(stop, FOLLOW_GROWTH_MS);
    // The moment the user takes over, the card stops chasing them.
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchmove', stop, { passive: true });

    return stop;
  }, [ref, messageKey, conversationId]);
};
